import browserStore from 'store2';

type CachedData<DataType> = { data: DataType; expiry?: number };

// On large-entitlement users the serialized entitled-entities payload can exceed
// the ~5MB per-origin sessionStorage quota, so every `session.set` throws
// QuotaExceededError. Because nothing then persists, `hasCacheExpired` stays true
// and `useContextSwitcherData` refetches + synchronously `JSON.stringify`-es the
// multi-MB payload on every dependency change (e.g. Auth0 token renewals), pinning
// the main thread until the tab freezes ("Page unresponsive"). Once a write fails
// we trip a session-level circuit breaker: stop touching sessionStorage and serve
// an in-memory copy instead, so the cache stays functional and the refetch/
// re-stringify loop is broken. Small-entitlement users are unaffected.
const inMemoryCache = new Map<string, CachedData<unknown>>();
let sessionStorageDisabled = false;

export const cacheDataInSessionStorage = <DataType>(cacheKey: string, cacheData: DataType, expiry?: number) => {
  const now = new Date().getTime();

  const cacheItem: CachedData<DataType> = {
    data: cacheData,
    expiry: expiry ? now + expiry : null
  };

  if (sessionStorageDisabled) {
    inMemoryCache.set(cacheKey, cacheItem);
    return;
  }

  try {
    browserStore.session.set(cacheKey, cacheItem);
    inMemoryCache.delete(cacheKey);
  } catch (e) {
    sessionStorageDisabled = true;
    inMemoryCache.set(cacheKey, cacheItem);
    console.error('Error storing entitled entities data in session storage', e);
  }
};

export const retrieveCachedDataFromSessionStorage = <DataType>(cacheKey: string): CachedData<DataType> => {
  const inMemoryItem = inMemoryCache.get(cacheKey);
  if (inMemoryItem) {
    return inMemoryItem as CachedData<DataType>;
  }
  let cachedData = null;
  try {
    cachedData = browserStore.session.get(cacheKey);
  } catch (error) {
    console.error('Error retrieving entitled entities data from session storage', error);
  }
  return cachedData;
};

export const hasCacheExpired = (cacheKey: string): boolean => {
  const now = new Date().getTime();

  let hasExpired = true;

  try {
    const cachedData = retrieveCachedDataFromSessionStorage(cacheKey);
    if (cachedData && cachedData.expiry > now) {
      hasExpired = false;
    }
  } catch (error) {
    console.error('Error checking if cache has expired', error);
  }
  return hasExpired;
};

export const clearCachedSessionData = (cacheKey: string): boolean => {
  let success = false;
  try {
    inMemoryCache.delete(cacheKey);
    browserStore.session.remove(cacheKey);
    success = true;
  } catch (error) {
    console.error('Error clearing cache', error);
    success = false;
  }
  return success;
};
